import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Sparkles, X } from 'lucide-react'
import type { RootState } from '../store'
import { useGetUsageQuery, useGetUserSettingsQuery, useUpdateUserSettingsMutation } from '../services/projectDataApi'
import { useNavigate } from './nav'
import { useEmbedded } from '../lib/embed'
import { trackEvent } from '../lib/analytics'
import { trackSegmentEvent } from '../lib/segment'
import { CREDIT_EQUIVALENCE, formatCreditCount } from '../lib/creditMeter'
import ModalPortal from './ModalPortal'
import './WelcomeModal.css'

/** A free plan was provisioned, so the copy below is about a plan they already have. */
export function isFreePlan(planKey: string | null | undefined): boolean {
  return typeof planKey === 'string' && planKey.startsWith('free')
}

/**
 * WelcomeModal — shown once, to a brand-new account, to say what they were given.
 *
 * Signing up no longer involves choosing a plan: the backend puts every new account on Free as it is
 * created (ensureUserForAuth), so the first thing a new user sees is the product, with credits they were
 * never told about. This is that telling — what the plan includes, what a credit buys, and where the
 * balance lives — plus a way to look at the other plans without going hunting for them.
 *
 * Shown once per ACCOUNT, not per device: `welcome_pending` comes from user_settings (see
 * lib/user-settings.ts), and the dialog reports itself shown as it opens. Everything it claims is checked
 * against the account first — it waits for the balance to load and stays away unless the plan really is
 * a free one, so the rare account without one (an SSO member who joined someone else's org) is never
 * told about a plan it doesn't have.
 *
 * Rendered once at the app root (App.tsx), like OutOfCreditsModal.
 */
export default function WelcomeModal() {
  const location = useLocation()
  const navigate = useNavigate()
  const embedded = useEmbedded()
  const user = useSelector((s: RootState) => s.auth.user)

  const settingsQuery = useGetUserSettingsQuery(undefined, { skip: !user })
  const pending = settingsQuery.data?.welcome_pending === true && !embedded
  // Only fetched for the few accounts that still owe a welcome, so this costs nothing for everyone else.
  const usageQuery = useGetUsageQuery(undefined, { skip: !pending })
  const [saveSettings] = useUpdateUserSettingsMutation()

  const ownAccount = usageQuery.data?.teams?.find((team) => team.is_owner) ?? null
  const credits = ownAccount?.monthly_credits ?? null
  const ready = pending && isFreePlan(ownAccount?.plan_key) && credits != null

  // Latched rather than derived: reporting the dialog shown flips `welcome_pending` false, which would
  // otherwise close it out from under the person reading it.
  const [open, setOpen] = useState(false)
  const openedRef = useRef(false)
  useEffect(() => {
    if (!ready || openedRef.current) return
    openedRef.current = true
    setOpen(true)
    trackEvent('welcome-modal-shown', { credits })
    trackSegmentEvent('Shown welcome modal')
    // Marked on open, not on dismiss: someone who closes the tab has still been told, and a dialog that
    // returns every session would read as a bug. A failed write only means they see it once more.
    void saveSettings({ welcome_shown: true })
  }, [ready, credits, saveSettings])

  const dismiss = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, dismiss])

  const viewPlans = useCallback(() => {
    trackEvent('upgrade-cta-clicked', { source: 'welcome-modal' })
    trackSegmentEvent('Clicked View Plans', { source: 'welcome-modal' })
    setOpen(false)
    // Keep this page as the background so closing settings returns here rather than to the project list.
    navigate('/settings/subscription', {
      state: { backgroundLocation: { pathname: location.pathname, search: location.search, hash: location.hash } },
    })
  }, [navigate, location.pathname, location.search, location.hash])

  if (!open || credits == null) return null

  return (
    <ModalPortal>
      <div className="welcome-modal__overlay" onClick={dismiss} data-testid="welcome-modal">
        <div
          className="welcome-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="welcome-modal__close"
            onClick={dismiss}
            aria-label="Close"
            data-testid="welcome-modal-close"
            data-analytics-event="welcome-modal-close"
          >
            <X size={18} aria-hidden />
          </button>

          <div className="welcome-modal__head">
            <div className="welcome-modal__icon" aria-hidden>
              <Sparkles size={22} strokeWidth={1.75} />
            </div>
            <h1 id="welcome-modal-title" className="welcome-modal__title">
              You’re on the Free plan
            </h1>
            <p className="welcome-modal__subtitle">
              Your account is ready — no card, no setup step.
            </p>
          </div>

          {/* The one number worth reading twice, so it carries the block on its own. */}
          <div className="welcome-modal__credits">
            <span className="welcome-modal__credits-figure">{formatCreditCount(credits)}</span>
            <span className="welcome-modal__credits-label">
              credits included
              <small>Each one covers {CREDIT_EQUIVALENCE}.</small>
            </span>
          </div>

          <ul className="welcome-modal__points">
            <li>Point us at your app and we’ll explore it, then report the bugs worth your time.</li>
            <li>Your balance stays in the top bar of every project, with what it went on.</li>
          </ul>

          <div className="welcome-modal__actions">
            <button
              type="button"
              className="welcome-modal__start"
              onClick={dismiss}
              data-analytics-event="welcome-modal-start"
              data-testid="welcome-modal-start"
            >
              Start testing
            </button>
            <button
              type="button"
              className="welcome-modal__view-plans"
              onClick={viewPlans}
              data-analytics-event="welcome-modal-view-plans"
              data-testid="welcome-modal-view-plans"
            >
              View plans
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}
